import { z } from 'zod';
import { currencyCodeSchema, DEFAULT_CURRENCY, minorAmountSchema, moneySchema } from './money.js';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from './constants.js';
import { emailSchema, skillSchema, userStatusSchema, verificationStatusSchema, } from './schemas.js';
/**
 * The Consent → Apply → Assign spine.
 *
 * These contracts are shared by the api and both frontends, so a field can
 * never drift between what the server sends and what a screen expects.
 */
/* -------------------------------------------------------------------------- */
/* Pagination envelope                                                        */
/* -------------------------------------------------------------------------- */
/** Every list endpoint returns this shape. */
export function pageSchema(item) {
    return z.object({
        items: z.array(item),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
        total: z.number().int().min(0),
        totalPages: z.number().int().min(0),
        hasMore: z.boolean(),
    });
}
export const pageQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
/**
 * A repeated or comma-joined query parameter, as one array.
 *
 * Express hands `?a=1&a=2` over as an array and `?a=1` as a bare string, so a
 * plain `z.array` rejects the single-value case — which is the common one.
 * Accepting `?a=1,2` as well keeps the URLs short enough to read.
 */
function queryUuidList(max) {
    return z.preprocess((value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value), z.array(z.uuid()).max(max));
}
/**
 * A query-string flag.
 *
 * Not `z.coerce.boolean()`: that is `Boolean(value)`, which turns the string
 * "false" into true — the one answer a flag must never get wrong.
 */
const queryBoolean = z.preprocess((value) => (typeof value === 'string' ? value === 'true' || value === '1' : value), z.boolean());
/* -------------------------------------------------------------------------- */
/* Enums mirrored from the database                                           */
/* -------------------------------------------------------------------------- */
export const applicationStatusSchema = z.enum(['SUBMITTED', 'SHORTLISTED', 'ACCEPTED', 'DECLINED']);
export const assignmentStatusSchema = z.enum([
    'ASSIGNED',
    'ACCEPTED',
    'IN_PROGRESS',
    'COMPLETED',
    'APPROVED',
]);
export const consentStatusSchema = z.enum(['PENDING', 'APPROVED', 'DECLINED']);
export const consentTypeSchema = z.enum(['APPLY_CONSENT', 'COMPLETION_APPROVAL']);
/** A job as it appears inside another resource. Never the full record. */
export const jobRefSchema = z.object({
    id: z.string(),
    title: z.string(),
    category: z.string(),
    location: z.string().nullable(),
    pay: moneySchema,
    posterName: z.string(),
    /** The employer, so a guardian can act on them (trust, block). */
    posterId: z.string(),
});
export const teenRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    photoUrl: z.string().nullable(),
    isMinor: z.boolean(),
    skills: z.array(z.string()),
});
/* -------------------------------------------------------------------------- */
/* Trust signals — the adult connection graph                                  */
/*                                                                            */
/* A teen never has social connections of their own. What makes a job feel     */
/* safe is what the adults around them already know: whether their guardian    */
/* trusts this employer, and whether other parents that guardian knows do.     */
/* That is the whole point — it turns a parent's approval from a guess into a  */
/* decision backed by people they know. [Parent steps 25-27]                   */
/* -------------------------------------------------------------------------- */
export const trustedConnectionStatusSchema = z.enum(['PENDING', 'CONNECTED', 'BLOCKED']);
export const parentConnectionStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'DECLINED', 'BLOCKED']);
/** What a viewer is told about an employer, without naming anyone's children. */
export const trustSignalSchema = z.object({
    /** The viewing teen's guardian has marked this employer trusted. */
    guardianTrusts: z.boolean(),
    /** Parents in the guardian's network who also trust them. */
    mutualParentCount: z.number().int().min(0),
    /** First names only — enough to recognise, not enough to identify a child. */
    mutualParentNames: z.array(z.string()).max(3),
    /** Times this teen has already completed work for this employer. */
    workedTogetherCount: z.number().int().min(0),
    /** The guardian has blocked them; the job should not be encouraged. */
    guardianBlocked: z.boolean(),
});
export const setTrustedConnectionInputSchema = z.object({
    jobPosterId: z.uuid(),
    status: z.enum(['CONNECTED', 'BLOCKED']),
});
export const trustedConnectionSchema = z.object({
    id: z.string(),
    status: trustedConnectionStatusSchema,
    jobPoster: z.object({
        id: z.string(),
        name: z.string(),
        professionalTitle: z.string().nullable(),
        location: z.string().nullable(),
        /**
         * The business this employer posts as, or null when they post as
         * themselves. It is the only thing separating the two kinds of connection
         * a guardian makes. [Parent steps 25-26]
         */
        organizationName: z.string().nullable(),
        organizationWebsite: z.string().nullable(),
    }),
    connectedAt: z.string(),
    /** How many of this parent's teens have worked for them. */
    jobsCompleted: z.number().int().min(0),
});
export const requestParentConnectionInputSchema = z.object({
    /** The other parent's email — how one guardian finds another. */
    email: emailSchema,
});
export const parentConnectionDecisionInputSchema = z.object({
    decision: z.enum(['ACCEPT', 'DECLINE', 'BLOCK']),
});
export const parentConnectionSchema = z.object({
    id: z.string(),
    status: parentConnectionStatusSchema,
    /** The other parent in the pair, whichever side the viewer is on. */
    parent: z.object({
        id: z.string(),
        name: z.string(),
        city: z.string().nullable(),
        state: z.string().nullable(),
        photoUrl: z.string().nullable(),
    }),
    /** True when the viewer received the request and owes an answer. */
    incoming: z.boolean(),
    requestedAt: z.string(),
    respondedAt: z.string().nullable(),
    /** Employers both parents trust — the LinkedIn "mutual" line. */
    mutualTrustedCount: z.number().int().min(0),
});
export const connectionListQuerySchema = pageQuerySchema.extend({
    status: z.string().optional(),
    /**
     * Which side of the employer list to read. The diagram splits these into two
     * steps because a guardian thinks about them differently: a neighbour who
     * needs a lawn mowed is not the same decision as a shop that hires.
     * [Parent steps 25-26]
     */
    kind: z.enum(['INDIVIDUAL', 'ORGANIZATION']).optional(),
});
/* -------------------------------------------------------------------------- */
/* Shortlist — Teen step 8                                                    */
/* -------------------------------------------------------------------------- */
export const saveOpportunityInputSchema = z.object({
    jobId: z.uuid(),
    /** Set by a parent saving on a teen's behalf. [Parent step 6] */
    savedForTeenId: z.uuid().optional(),
});
export const savedOpportunitySchema = z.object({
    id: z.string(),
    job: jobRefSchema,
    savedAt: z.string(),
    /** Who saved it, when that was not the viewer. */
    savedByName: z.string().nullable(),
    /**
     * When the teen first opened a job their guardian sent them, or null if they
     * have not yet. Only ever set for a shared row — a teen does not "open" the
     * jobs they saved themselves. It is what turns a guardian's shortlist from a
     * list of things they sent into a list of things that landed.
     * [Parent steps 6-7, Teen step 6]
     */
    openedAt: z.string().nullable(),
});
/**
 * Reading a shortlist.
 *
 * A guardian passing `teenId` reads that teen's whole shortlist rather than
 * only the jobs they themselves put on it — which is the difference between
 * "what I suggested" and "what my teen is actually looking at". [Parent step 7]
 */
export const savedListQuerySchema = pageQuerySchema.extend({
    teenId: z.uuid().optional(),
});
/* -------------------------------------------------------------------------- */
/* Parent consent — Teen step 9, Parent steps 9-10                            */
/* -------------------------------------------------------------------------- */
export const requestConsentInputSchema = z.object({
    jobId: z.uuid(),
    consentType: consentTypeSchema.default('APPLY_CONSENT'),
    /** Optional note from the teen, e.g. why they want this job. */
    message: z.string().trim().max(500).optional(),
});
export const consentDecisionInputSchema = z.object({
    decision: z.enum(['APPROVE', 'DECLINE']),
    /** Shown to the teen, so a decline can be explained. */
    note: z.string().trim().max(500).optional(),
});
export const consentRequestSchema = z.object({
    id: z.string(),
    status: consentStatusSchema,
    consentType: consentTypeSchema,
    job: jobRefSchema,
    teen: teenRefSchema,
    requestedAt: z.string(),
    respondedAt: z.string().nullable(),
    /** Why this guardian can say yes with confidence. */
    trust: trustSignalSchema,
});
export const consentListQuerySchema = pageQuerySchema.extend({
    status: consentStatusSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* Applications — Teen step 10, Job Poster steps 11-15                        */
/* -------------------------------------------------------------------------- */
export const submitApplicationInputSchema = z.object({
    jobId: z.uuid(),
    /** Teen's pitch for this job. */
    coverNote: z.string().trim().max(1000).optional(),
    /** URLs of supporting documents. Uploads land in a later module. */
    documentUrls: z.array(z.string().trim().max(500)).max(5).optional(),
});
export const applicationSchema = z.object({
    id: z.string(),
    status: applicationStatusSchema,
    job: jobRefSchema,
    teen: teenRefSchema,
    coverNote: z.string().nullable(),
    documentUrls: z.array(z.string()),
    appliedAt: z.string(),
    /** Null for an adult teen, who needs no consent. */
    consentStatus: consentStatusSchema.nullable(),
    /** True when this application already has an assignment. */
    assigned: z.boolean(),
});
export const applicationListQuerySchema = pageQuerySchema.extend({
    status: applicationStatusSchema.optional(),
    /** Job poster only: narrow to one of their jobs. */
    jobId: z.uuid().optional(),
    /* Shortlisting filters — Job Poster step 13 ------------------------------
     * A poster choosing between forty applicants needs to cut the list down by
     * the things they are actually choosing on. All are poster-only and all
     * combine, because "has these skills AND is rated well" is the real question.
     */
    /** `skills.public_id` values. An applicant must hold every one named. */
    skillIds: queryUuidList(10).optional(),
    /** Average review rating floor, 1-5. Unrated teens are excluded when set. */
    minRating: z.coerce.number().min(1).max(5).optional(),
    /** Only teens who have filled in when they can work. */
    hasAvailability: queryBoolean.optional(),
    sortBy: z.enum(['appliedAt', 'status']).default('appliedAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export const applicationDecisionInputSchema = z.object({
    decision: z.enum(['SHORTLIST', 'DECLINE']),
});
/* -------------------------------------------------------------------------- */
/* Assignments — Job Poster steps 16-20, Teen steps 13-14                     */
/* -------------------------------------------------------------------------- */
export const createAssignmentInputSchema = z.object({
    jobId: z.uuid(),
    /** The teens to assign. One entry for SINGLE_TEEN, up to `teensNeeded`. */
    teenIds: z.array(z.uuid()).min(1).max(50),
    /**
     * Agreed pay per teen, in minor units. Defaults to the job's own pay when
     * omitted; it can differ when several teens are on one job.
     */
    agreedPayMinor: z.number().int().min(0).optional(),
});
export const assignmentResponseInputSchema = z.object({
    response: z.enum(['ACCEPT', 'DECLINE']),
});
export const assignmentSchema = z.object({
    id: z.string(),
    status: assignmentStatusSchema,
    job: jobRefSchema,
    teen: teenRefSchema,
    agreedPay: moneySchema,
    paymentStatus: z.enum(['UNPAID', 'RELEASED', 'FAILED']),
    assignedAt: z.string(),
    acceptedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    /**
     * What the employer wants the teen to know before they turn up. Carried on
     * the assignment rather than the job listing, because that is exactly the
     * point at which it should become visible. [Job Poster step 22]
     */
    instructions: z.string().nullable(),
    /**
     * When a guardian signed off that the finished work matched what was asked,
     * or null. Advisory — it never gates the employer's approval or the payment.
     * [Parent step 18]
     */
    guardianConfirmedAt: z.string().nullable(),
});
export const assignmentListQuerySchema = pageQuerySchema.extend({
    status: assignmentStatusSchema.optional(),
    jobId: z.uuid().optional(),
});
/* -------------------------------------------------------------------------- */
/* Jobs — Job Poster steps 5-10, Teen step 7                                  */
/* -------------------------------------------------------------------------- */
export const jobNeedTypeSchema = z.enum(['SINGLE_TEEN', 'MULTIPLE_TEENS']);
export const jobPayTypeSchema = z.enum(['FIXED', 'BID']);
export const jobStatusSchema = z.enum([
    'DRAFT',
    'LIVE',
    'IN_PROGRESS',
    'COMPLETED',
    'CLOSED',
    'FLAGGED',
]);
export const createJobInputSchema = z
    .object({
    title: z.string().trim().min(1, 'Title is required').max(200),
    description: z.string().trim().min(1, 'Description is required').max(5000),
    category: z.string().trim().min(1, 'Category is required').max(100),
    location: z.string().trim().max(255).optional(),
    streetAddress: z.string().trim().max(255).optional(),
    city: z.string().trim().max(100).optional(),
    state: z.string().trim().max(100).optional(),
    zipCode: z.string().trim().max(20).optional(),
    country: z.string().trim().max(100).default('United States'),
    /** `skills.public_id` values the job asks for. */
    skillIds: z.array(z.uuid()).max(20).optional(),
    /** Free-form date/time window until the scheduling module lands. */
    schedule: z.record(z.string(), z.unknown()).optional(),
    /**
         * Address notes, materials, dress code — shared only once a teen is
         * assigned. Free-form JSON for future structure; `notes` is the one key
         * the product reads and writes today. [Job Poster step 22]
         */
    instructions: z.record(z.string(), z.unknown()).optional(),
    needType: jobNeedTypeSchema.default('SINGLE_TEEN'),
    teensNeeded: z.number().int().min(1).max(50).default(1),
    payType: jobPayTypeSchema,
    /** Per-teen budget in minor units. Never a float. */
    payAmountMinor: minorAmountSchema,
    currency: currencyCodeSchema.default(DEFAULT_CURRENCY),
    /** DRAFT keeps it private; LIVE publishes it to teens. */
    status: z.enum(['DRAFT', 'LIVE']).default('DRAFT'),
})
    .refine((value) => value.needType === 'MULTIPLE_TEENS' || value.teensNeeded === 1, {
    message: 'A single-teen job can only need one teen',
    path: ['teensNeeded'],
});
export const jobSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    category: z.string(),
    location: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zipCode: z.string().nullable(),
    pay: moneySchema,
    payType: jobPayTypeSchema,
    needType: jobNeedTypeSchema,
    teensNeeded: z.number().int(),
    /** How many assignment slots are already taken. */
    teensAssigned: z.number().int(),
    status: jobStatusSchema,
    posterName: z.string(),
    posterId: z.string(),
    /**
     * Skills the job asks for, as objects rather than names — a poster
     * filtering applicants by skill needs the id, and the teen profile already
     * carries this shape. [Job Poster steps 5, 13]
     */
    skills: z.array(skillSchema),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    /** Viewer-relative flags, so a card can render without extra calls. */
    savedByViewer: z.boolean(),
    appliedByViewer: z.boolean(),
    /** Teen viewers only: whether guardian consent is already approved. */
    consentStatus: consentStatusSchema.nullable(),
    /** What the adults around this viewer already know about the employer. */
    trust: trustSignalSchema,
});
export const jobListQuerySchema = pageQuerySchema.extend({
    /** Free-text over title and description. */
    q: z.string().trim().max(100).optional(),
    category: z.string().trim().max(100).optional(),
    city: z.string().trim().max(100).optional(),
    state: z.string().trim().max(100).optional(),
    payType: jobPayTypeSchema.optional(),
    minPayMinor: z.coerce.number().int().min(0).optional(),
    maxPayMinor: z.coerce.number().int().min(0).optional(),
    /** Job poster only: include their drafts. */
    status: jobStatusSchema.optional(),
    sortBy: z.enum(['publishedAt', 'createdAt', 'payAmountMinor', 'title']).default('publishedAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
/* -------------------------------------------------------------------------- */
/* Bids — Job Poster steps 9, 12-14; Teen step 7 (bid-enabled jobs)           */
/* -------------------------------------------------------------------------- */
export const bidStatusSchema = z.enum(['SUBMITTED', 'ACCEPTED', 'REJECTED']);
export const submitBidInputSchema = z.object({
    jobId: z.uuid(),
    /** Minor units of `currency`. Never a float. */
    bidAmountMinor: minorAmountSchema,
    currency: currencyCodeSchema.default(DEFAULT_CURRENCY),
});
export const bidSchema = z.object({
    id: z.string(),
    status: bidStatusSchema,
    job: jobRefSchema,
    teen: teenRefSchema,
    bidAmount: moneySchema,
    submittedAt: z.string(),
});
export const bidListQuerySchema = pageQuerySchema.extend({
    status: bidStatusSchema.optional(),
    /** Job poster only: narrow to one of their jobs. */
    jobId: z.uuid().optional(),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export const bidDecisionInputSchema = z.object({
    decision: z.enum(['ACCEPT', 'REJECT']),
});
/* -------------------------------------------------------------------------- */
/* Earnings — Parent step 20, Teen dashboard                                  */
/* -------------------------------------------------------------------------- */
export const earningsHistoryEntrySchema = z.object({
    assignmentId: z.string(),
    job: jobRefSchema,
    amount: moneySchema,
    releasedAt: z.string(),
});
export const earningsSummarySchema = z.object({
    totalEarned: moneySchema,
    pendingPayout: moneySchema,
    completedJobs: z.number().int(),
    history: z.array(earningsHistoryEntrySchema),
});
/* -------------------------------------------------------------------------- */
/* Parent console                                                              */
/*                                                                            */
/* A guardian's home. Everything they are responsible for, and every decision  */
/* waiting on them, in one shape so the console renders from one call.         */
/* -------------------------------------------------------------------------- */
export const parentTeenSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    photoUrl: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    isMinor: z.boolean(),
    /** The teen's account status — PENDING until this guardian approves them. */
    status: userStatusSchema,
    /** mother | father | guardian. */
    relationshipType: z.string(),
    /** False while the guardian link itself is still unconfirmed. */
    verified: z.boolean(),
    /** Work in flight, so a parent can see activity without opening each teen. */
    openApplications: z.number().int().min(0),
    activeAssignments: z.number().int().min(0),
    /** Job permissions this teen is waiting on from this guardian. */
    pendingConsents: z.number().int().min(0),
});
export const parentOverviewSchema = z.object({
    teens: z.array(parentTeenSummarySchema),
    /** Counts that drive the console's action tiles and nav badges. */
    pendingJobApprovals: z.number().int().min(0),
    pendingTeenAccounts: z.number().int().min(0),
    trustedEmployers: z.number().int().min(0),
    blockedEmployers: z.number().int().min(0),
    connectedParents: z.number().int().min(0),
    /** Connection requests from other guardians awaiting an answer. */
    incomingConnectionRequests: z.number().int().min(0),
});
/* -------------------------------------------------------------------------- */
/* Teen console                                                                */
/*                                                                            */
/* A teen's home. What is waiting on them, what they are waiting on someone    */
/* else for, and what they have going. Money lives in the earnings module —    */
/* the console reads it from there rather than recomputing it. [Teen step 20]  */
/* -------------------------------------------------------------------------- */
export const teenOverviewSchema = z.object({
    /** Offers waiting on a yes or no — the only thing that truly blocks. */
    jobOffers: z.number().int().min(0),
    /** Accepted or under way. */
    activeJobs: z.number().int().min(0),
    completedJobs: z.number().int().min(0),
    /** Sent to an employer, no decision yet. */
    openApplications: z.number().int().min(0),
    shortlistedApplications: z.number().int().min(0),
    /** Guardian has not answered these yet. */
    awaitingGuardian: z.number().int().min(0),
    /** Approved by a guardian but not applied for — the easiest next win. */
    readyToApply: z.number().int().min(0),
    savedJobs: z.number().int().min(0),
    /** Profile signals, so the console can nudge without a second call. */
    skillCount: z.number().int().min(0),
    hasBio: z.boolean(),
    hasAvailability: z.boolean(),
    /** Guardian state, for the banner a blocked teen needs to see. */
    isMinor: z.boolean(),
    guardianApproved: z.boolean(),
    guardianName: z.string().nullable(),
});
/* -------------------------------------------------------------------------- */
/* Job poster console                                                          */
/*                                                                            */
/* An employer's home. What they have posted, who is waiting on a decision,    */
/* and what they have committed to pay. [Job Poster journey]                   */
/* -------------------------------------------------------------------------- */
export const posterOverviewSchema = z.object({
    liveJobs: z.number().int().min(0),
    draftJobs: z.number().int().min(0),
    inProgressJobs: z.number().int().min(0),
    completedJobs: z.number().int().min(0),
    /** Applications nobody has looked at yet — the queue that matters. */
    newApplications: z.number().int().min(0),
    shortlisted: z.number().int().min(0),
    /** Open bids awaiting a decision, for BID-type jobs. */
    openBids: z.number().int().min(0),
    teensAssigned: z.number().int().min(0),
    /** Assigned but not yet accepted by the teen. */
    awaitingTeenResponse: z.number().int().min(0),
    /** Work the teen has marked finished, waiting on the employer to sign off. */
    awaitingApproval: z.number().int().min(0),
    /** Signed off but not paid — the last thing standing between work and money. */
    awaitingPayment: z.number().int().min(0),
    /** Agreed pay on work under way — money promised, not yet released. */
    committedSpend: moneySchema,
    /** Agreed pay on finished work. */
    paidOut: moneySchema,
    /** Employers are verified before teens are encouraged to trust them. */
    verificationStatus: verificationStatusSchema,
    /** Guardians who have marked this employer trusted — social proof. */
    trustedByParents: z.number().int().min(0),
});
/* -------------------------------------------------------------------------- */
/* Favourite teens — Job Poster step 30                                       */
/* -------------------------------------------------------------------------- */
/**
 * An employer's shortlist of people, as opposed to jobs.
 *
 * The point is repeat work: someone who did well on a Saturday should be one
 * click away the next time, rather than something the employer has to remember
 * a name for.
 */
export const favouriteTeenSchema = z.object({
    id: z.string(),
    teen: teenRefSchema,
    savedAt: z.string(),
    /** How many jobs this employer has already had them do. */
    jobsTogether: z.number().int().min(0),
});
export const favouriteTeenInputSchema = z.object({
    /** The teen's publicId. */
    teenId: z.uuid(),
});
//# sourceMappingURL=marketplace.js.map